import { Button, DatePicker, Radio, RadioChangeEvent, Space } from "antd";
import { RangePickerProps } from "antd/es/date-picker";
import dayjs, { Dayjs } from "dayjs";
import { ReactNode, useEffect, useState } from "react";
import { useAppSelector } from "../../../../app/store";
import CustomModal from "../../../../components/ui/CustomModal";
import Loader from "../../../../components/ui/Loader";
import { useGetSingleCompanyQuery } from "../../../../features/portal/companyOnboard/companyOnboardApi";
import { useBankRequestMutation } from "../../../../features/portal/statements/statementsApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import ConfirmModalLayout from "../../../../layouts/ConfirmModalLayout";
import { toast } from "../../../../lib/toast";
import AlertIcon from "../_icons/AlertIcon";
import ListIcon from "../_icons/ListIcon";
import LimitWarningModal from "./LimitWarningModal";

const { RangePicker } = DatePicker;
type RangeValue = RangePickerProps["value"];

export interface ModalConfirmProps<
  DataT = never,
  TypeT = "SUCCESS" | "ADD" | "UPDATE" | "VIEW" | "CONFIRM" | "SELECT_STATEMENT"
> {
  open: boolean;
  action?: boolean;
  buttonBgDeep?: boolean;
  handleClose: (val?: any) => void;
  type?: TypeT;
  data?: DataT;
  title?: ReactNode;
  subTitle?: ReactNode;
  buttonName?: string;
  loading?: boolean;
  children?: ReactNode;
  batchLogData?: any;
}

export default function SelectStatementModal({
  title,
  action,
  buttonName,
  buttonBgDeep,
  subTitle,
  handleClose,
  children,
  type,
  data,
  loading,
  batchLogData,

  ...rest
}: ModalConfirmProps) {
  const [value, setValue] = useState<number>(7);
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(6, "day"),
    dayjs(),
  ]);

  const {
    open: openConfirmModal,
    handleOpen: handleConfirmOpen,
    handleClose: handleConfirmClose,
  } = useModal<undefined, "CONFIRM">();

  const {
    open: openWarningModal,
    handleOpen: handleWarningOpen,
    handleClose: handleWarningClose,
  } = useModal<undefined, "WARNING">();

  const { user } = useAppSelector((state) => state.auth);

  const [bankRequest, { isLoading, isSuccess, isError, error }] =
    useBankRequestMutation();
  const {
    isLoading: isCompanyDetailsLoading,
    isError: isCompanyDetailsError,
    error: companyDetailsError,
    data: companyDetails,
  } = useGetSingleCompanyQuery(user?.data?.company_id, {
    skip: !user?.data?.company_id,
  });

  useError(isError, error, "Bank request fail");
  useError(isCompanyDetailsError, companyDetailsError, "Get company data fail");

  const onChange = (e: RadioChangeEvent) => {
    const val = e.target.value;
    setValue(val);

    if (val === 10) {
      // Set default 10-day range when "Custom" selected
      setRange([dayjs().subtract(9, "day"), dayjs()]);
    } else if (val !== 0) {
      // Set range for other predefined options
      setRange([dayjs().subtract(val - 1, "day"), dayjs()]);
    }
  };

  const handleRangeChange = (dates: RangeValue) => {
    if (dates && dates[0] && dates[1]) {
      setRange([dates[0], dates[1]]);
    }
  };

  const getDayCountText = (range: [Dayjs, Dayjs]) => {
    const [start, end] = range;
    const days = end.diff(start, "day") + 1;
    return `- ${days} Day${days > 1 ? "s" : ""}`;
  };

  const formatDateRangeText = (range: [Dayjs, Dayjs]) => {
    const [start, end] = range;
    return `${start.format("D MMMM, YYYY")} - ${end.format("D MMMM, YYYY")}`;
  };

  const handleSendToApprove = () => {
    const [start, end] = range;
    const walletNumber = companyDetails?.data?.wallet_number;

    if (!walletNumber) {
      toast("warning", "Please add Sheba Pay wallet");
      return;
    }

    const newData = {
      page: 1,
      limit: 100000000,
      msisdn: String(walletNumber),
      userType: "Merchant",
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
      custom: value === 10,
    };

    bankRequest(newData);
  };

  const handleSendToWarning = () => {
    handleWarningClose();
    handleClose();
  };

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Statement Request Successful");
      handleClose();
      handleConfirmClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <>
      {openConfirmModal?.type === "CONFIRM" && (
        <ConfirmModalLayout
          title="Generate Statement"
          subTitle="Are you sure you want to generate a new statement for the selected date range?"
          buttonName="Generate"
          handleButton={handleSendToApprove}
          open={openConfirmModal?.open}
          handleClose={handleConfirmClose}
          action
          buttonBgDeep
          loading={isLoading}
        >
          <div>
            <ul>
              <li className="mb-3 mt-4 text-[#5E718D] flex gap-2 items-center">
                <span>
                  <ListIcon />
                </span>
                <span>{formatDateRangeText(range)}</span>
                <span>{getDayCountText(range)}</span>
              </li>
            </ul>
          </div>
        </ConfirmModalLayout>
      )}

      {openWarningModal?.type === "WARNING" && (
        <LimitWarningModal
          title="Daily Limit Reached"
          subTitle="You have already requested statements 3 times today.
Please try again tomorrow or check your previously generated statements."
          buttonName="Okay"
          handleButton={handleSendToWarning}
          open={openWarningModal?.open}
          handleClose={handleWarningClose}
          action
          loading={false}
        />
      )}

      <CustomModal
        {...rest}
        handleClose={handleClose}
        width={440}
        footer={null}
      >
        {isCompanyDetailsLoading ? (
          <Loader />
        ) : (
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold">Statement Duration</span>
            </div>
            <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-zinc-200 mt-3"></div>

            <div className="mt-6">
              <Radio.Group
                onChange={onChange}
                value={value}
                className="flex flex-col gap-2"
              >
                <Radio value={7}>Last 7 Days</Radio>
                <Radio value={14}>Last 14 Days</Radio>
                <Radio value={30}>Last 30 Days</Radio>
                <Radio value={60}>Last 60 Days</Radio>
                <Radio value={10}>Custom</Radio>
              </Radio.Group>

              {value === 10 ? (
                <div className="flex items-center justify-start gap-3 mt-8">
                  <RangePicker
                    value={range}
                    onChange={handleRangeChange}
                    allowClear={false}
                    className="w-full"
                    disabledDate={(current) =>
                      current && current > dayjs().endOf("day")
                    }
                  />

                  {/* <div className="text-center justify-center text-slate-600 text-base font-normal !w-[100px]">
                    {getDayCountText(range)}
                  </div> */}
                </div>
              ) : (
                <div className="mt-8">
                  <RangePicker
                    value={range}
                    onChange={(dates: any) => setRange(dates)}
                    format="DD MMM, YYYY"
                    style={{ width: "100%" }}
                    disabled
                  />
                </div>
              )}

              <div className="flex items-center justify-start gap-3 self-stretch text-slate-600 text-sm font-normal mt-8">
                <span>
                  <AlertIcon />
                </span>
                <span>
                  You can request a statement up to 3 times per day and 10 times
                  per month. Preparing the statement may take up to 24 hours.
                </span>
              </div>

              <Space className="w-full mt-8" size={12}>
                <Button
                  className="normal-dark-button modal-button"
                  onClick={handleClose}
                  size="middle"
                >
                  Cancel
                </Button>
                <Button
                  className="modal-button"
                  type="primary"
                  htmlType="submit"
                  size="middle"
                  onClick={() => handleConfirmOpen({ type: "CONFIRM" })}
                  // onClick={() => handleWarningOpen({ type: "WARNING" })}
                  loading={loading}
                >
                  Request Statement
                </Button>
              </Space>
            </div>
          </div>
        )}
      </CustomModal>
    </>
  );
}
